import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Layers, Thermometer, Tag, Cctv, PencilRuler, X, Waypoints } from 'lucide-react';
import { baseapi, useApiOutage } from '../baseapi';
import { useTheme } from '../context/ThemeContext';
import { CameraStreamModal } from '../components/junctions/CameraStreamModal';
import sketch from '../qlines/qlinesData.json';
import { ZoneStrip } from '../components/queue/ZoneStrip';

/**
 * Q-Lines tab: a schematic of the Dasara Q-line plan (traced from the Devasthanam DWG) with the
 * numbered camera positions of the plan. Each position can be bound to a console camera; bound
 * positions show live status, people in view and today's counts, and colour the Q-line stretch
 * they watch (heat). Nothing is estimated: unbound positions stay grey.
 */

type Pt = [number, number];
interface Route { id: string; category: string; points: Pt[]; length_units: number }
interface CamPos { key: string; numbers: number[]; short: string; x: number; y: number }
interface Landmark { name: string; kind: string; x: number; y: number }
interface Category { id: string; name: string; color: string }
interface Sketch {
  width: number; height: number; source: string;
  categories: Category[]; routes: Route[]; cameras: CamPos[]; landmarks: Landmark[];
  context: Record<string, Pt[][]>;
}
const DATA = sketch as unknown as Sketch;

interface LiveCam {
  id: string; name: string; ip: string; checkpoint: string; state: string;
  tracks: number | null;                 // people/vehicles currently tracked in view
  today: { toward: number; away: number };
  m15: { toward: number; away: number };
}

// The blueprint render (public/qlines/blueprint.png, 2700x1800) placed in sketch units. Derived from the sheet
// frame: drawing x 5081.25..6203.47 / y 3977.5..4759.63 appears at pixels 162..2407 / 81..1645 (1.99966 px per unit).
const BLUEPRINT = { href: '/plan/blueprint.png', x: -99.8, y: -20.1, width: 1350.2, height: 900.2 };
const HEAT = { amber: 12, red: 30 };     // people in view -> stretch colour (until view_area_m2 is configured)
const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-IN'));

const camColor = (lc: LiveCam | undefined, bound: boolean) => {
  if (!bound) return '#64748b';
  if (!lc) return '#94a3b8';
  if (lc.state === 'online' || lc.state === 'replay') return '#22c55e';
  if (lc.state === 'stale' || lc.state === 'connecting') return '#f59e0b';
  if (lc.state === 'standby') return '#38bdf8';      // view-only camera: reachable, opens on demand
  return '#ef4444';
};
const heatColor = (inView: number | null) => {
  if (inView == null) return null;
  if (inView >= HEAT.red) return '#ef4444';
  if (inView >= HEAT.amber) return '#f59e0b';
  return '#22c55e';
};

/** squared distance from a point to a polyline (sketch units) */
function distToRoute(p: Pt, r: Route): number {
  let best = Infinity;
  for (let i = 1; i < r.points.length; i++) {
    const [x1, y1] = r.points[i - 1], [x2, y2] = r.points[i];
    const dx = x2 - x1, dy = y2 - y1, l2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((p[0] - x1) * dx + (p[1] - y1) * dy) / l2));
    const ex = x1 + t * dx - p[0], ey = y1 + t * dy - p[1];
    best = Math.min(best, ex * ex + ey * ey);
  }
  return Math.sqrt(best);
}

export const QLinesPage: React.FC = () => {
  const { theme } = useTheme();
  const dark = theme === 'dark';

  // ---------------- live data ----------------
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [live, setLive] = useState<Record<string, LiveCam>>({});
  const [checkpoints, setCheckpoints] = useState<Record<string, any>>({});
  const [configCams, setConfigCams] = useState<Array<{ id: string; name: string; ip: string; checkpoint: string }>>([]);
  const [stale, setStale] = useState(false);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message

  useEffect(() => {
    baseapi.get('qlines/mapping').then(r => setMapping(r.data?.mapping || {})).catch(() => {});
    baseapi.get('config').then(r => setConfigCams((r.data?.cameras || []).map((c: any) => ({ id: c.id, name: c.name, ip: c.ip, checkpoint: c.checkpoint })))).catch(() => {});
  }, []);

  const poll = useCallback(async () => {
    try {
      const [cams, summary] = await Promise.all([baseapi.get('cameras'), baseapi.get('summary')]);
      const cps = summary.data?.checkpoints || {};
      const out: Record<string, LiveCam> = {};
      const names: Record<string, { name: string; ip: string; checkpoint: string }> = {};
      configCams.forEach(c => { names[c.id] = c; });
      Object.entries(cams.data || {}).forEach(([id, st]: [string, any]) => {
        out[id] = {
          id, name: names[id]?.name || id, ip: names[id]?.ip || '', checkpoint: names[id]?.checkpoint || '',
          state: st.state, tracks: st.state === 'online' ? (st.tracks ?? 0) : null,
          today: { toward: 0, away: 0 }, m15: { toward: 0, away: 0 },
        };
      });
      Object.values(cps).forEach((cp: any) => {
        Object.values(cp.lines || {}).forEach((ln: any) => {
          const c = out[ln.camera_id]; if (!c) return;
          c.today.toward += ln.people?.toward?.total || 0; c.today.away += ln.people?.away?.total || 0;
          c.m15.toward += ln.people?.toward?.m15 || 0; c.m15.away += ln.people?.away?.m15 || 0;
        });
      });
      setLive(out); setCheckpoints(cps); setStale(false);
    } catch { setStale(true); }
  }, [configCams]);
  useEffect(() => { poll(); const t = window.setInterval(poll, 3000); return () => window.clearInterval(t); }, [poll]);

  const saveMapping = async (key: string, camId: string) => {
    const next = { ...mapping }; if (camId) next[key] = camId; else delete next[key];
    setMapping(next);
    try { await baseapi.put('qlines/mapping', { mapping: next }); } catch { /* keep local state; retried on next change */ }
  };

  // ---------------- derived: camera position -> route, route heat ----------------
  const posRoute = useMemo(() => {
    const m: Record<string, string | null> = {};
    DATA.cameras.forEach(c => {
      let best: { id: string; d: number } | null = null;
      for (const route of DATA.routes) {
        const distance = distToRoute([c.x, c.y], route);
        if (best === null || distance < best.d) best = { id: route.id, d: distance };
      }
      const nearest = best as { id: string; d: number } | null;
      m[c.key] = nearest !== null && nearest.d < 30 ? nearest.id : null;
    });
    return m;
  }, []);
  const routeHeat = useMemo(() => {
    const h: Record<string, { inView: number; cams: string[] }> = {};
    DATA.cameras.forEach(c => {
      const rid = posRoute[c.key]; const camId = mapping[c.key]; const lc = camId ? live[camId] : undefined;
      if (!rid || !lc || lc.tracks == null) return;
      const e = h[rid] || (h[rid] = { inView: 0, cams: [] });
      e.inView = Math.max(e.inView, lc.tracks); e.cams.push(camId!);
    });
    return h;
  }, [posRoute, mapping, live]);

  // ---------------- view: pan / zoom ----------------
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [blueprint, setBlueprint] = useState(true);              // true: CAD blueprint image as base; false: traced schematic
  // Start with the drawing itself in the middle of the panel. The Q lines and cameras lie within x -2..1046,
  // y 40..771 of the 1280 x 860 sheet (the empty legend column on the right pushed them to the left): scale
  // that area to fit and centre it. Blueprint and schematic share the same coordinates.
  const HOME = { x: 76, y: -8, k: 1.08 };
  const [view, setView] = useState(HOME);                        // translate + scale in sketch units
  const drag = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const toSketch = (clientX: number, clientY: number) => {
    const svg = svgRef.current!; const r = svg.getBoundingClientRect();
    const sx = (clientX - r.left) / r.width * DATA.width, sy = (clientY - r.top) / r.height * DATA.height;
    return { sx, sy };
  };
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const { sx, sy } = toSketch(e.clientX, e.clientY);
    const f = e.deltaY < 0 ? 1.18 : 1 / 1.18;
    setView(v => {
      const k = Math.max(0.6, Math.min(12, v.k * f));
      // keep the point under the cursor fixed: sketch = (screen - t)/k
      const wx = (sx - v.x) / v.k, wy = (sy - v.y) / v.k;
      return { k, x: sx - wx * k, y: sy - wy * k };
    });
  };
  const onDown = (e: React.MouseEvent) => { drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y }; };
  const onMove = (e: React.MouseEvent) => {
    if (!drag.current || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    const d = drag.current;   // copy now: the updater below runs later, possibly after mouse-up cleared the ref
    const dx = (e.clientX - d.x) / r.width * DATA.width, dy = (e.clientY - d.y) / r.height * DATA.height;
    setView(v => ({ ...v, x: d.vx + dx, y: d.vy + dy }));
  };
  const onUp = () => { drag.current = null; };
  const zoomBy = (f: number) => setView(v => {
    const cx = DATA.width / 2, cy = DATA.height / 2; const k = Math.max(0.6, Math.min(12, v.k * f));
    const wx = (cx - v.x) / v.k, wy = (cy - v.y) / v.k; return { k, x: cx - wx * k, y: cy - wy * k };
  });
  const reset = () => setView(HOME);
  useEffect(() => { setView({ x: 76, y: -8, k: 1.08 }); }, [blueprint]);
  const focusCorridor = () => setView({ x: -40 * 1.9 + 0, y: -60 * 1.9, k: 1.9 });

  // ---------------- UI state ----------------
  const [showContext, setShowContext] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [showHeat, setShowHeat] = useState(true);
  const [assignMode, setAssignMode] = useState(false);
  const [hover, setHover] = useState<{ key: string; cx: number; cy: number } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [modal, setModal] = useState<{ cp: any; camId: string } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const openCamera = (key: string) => {
    const camId = mapping[key]; if (!camId) { setSelected(key); return; }
    const cp = Object.values(checkpoints).find((c: any) => (c.cameras || []).some((x: any) => x.id === camId));
    const lc = live[camId];
    setModal({
      camId,
      cp: cp || { id: lc?.checkpoint || camId, name: lc?.name || camId, cameras: [{ id: camId, state: lc?.state || 'unavailable', detail: `RTSP stream · ${lc?.name || camId}` }] },
    });
  };

  const catColor: Record<string, string> = useMemo(() => Object.fromEntries(DATA.categories.map(c => [c.id, c.color])), []);
  const bg = blueprint ? '#ffffff' : dark ? '#0b1118' : '#f8fafc';
  const ink = dark ? '#cbd5e1' : '#334155';
  const ctxStyle: Record<string, { stroke: string; width: number; dash?: string }> = {
    water: { stroke: dark ? '#1d4ed8' : '#60a5fa', width: 0.9 },
    hill: { stroke: dark ? '#a16207' : '#b45309', width: 1.1, dash: '4 3' },
    road: { stroke: dark ? '#475569' : '#94a3b8', width: 1.0 },
    building: { stroke: dark ? '#334155' : '#cbd5e1', width: 0.5 },
    steps: { stroke: dark ? '#1f2937' : '#e2e8f0', width: 0.35 },
    path: { stroke: dark ? '#3f3f46' : '#a1a1aa', width: 0.6, dash: '2 2' },
    procession: { stroke: dark ? '#fb923c' : '#ea580c', width: 1.2, dash: '5 3' },
  };

  const boundCount = DATA.cameras.filter(c => mapping[c.key]).length;
  const onlineCount = DATA.cameras.filter(c => mapping[c.key] && live[mapping[c.key]]?.state === 'online').length;
  const hoverPos = hover ? DATA.cameras.find(c => c.key === hover.key) : null;
  const hoverCam = hoverPos && mapping[hoverPos.key] ? live[mapping[hoverPos.key]] : undefined;
  const selPos = selected ? DATA.cameras.find(c => c.key === selected) : null;

  return (
    <div className="h-full flex flex-col gap-3 p-3 sm:p-4 min-h-0">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-black tracking-tight text-neutral-900 dark:text-neutral-100 flex items-center gap-2.5">
            <Cctv className="w-6 h-6 text-blue-500" /> Q-Lines · Indrakeeladri
          </h1>
          <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Traced from the Devasthanam plan "Q-LINE'S PLAN (DASARA)" · {DATA.cameras.length} camera positions ({boundCount} bound, {onlineCount} online)
            {stale && outage && <span className="ml-2 text-amber-500">· analytics API not reachable, showing last data</span>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs sm:text-[13px]">
          <div className="flex rounded-md overflow-hidden border border-neutral-200/70 dark:border-[#262b37]">
            {[['Blueprint', true], ['Schematic', false]].map(([l, v]) => (
              <button key={String(l)} onClick={() => { setBlueprint(v as boolean); setShowContext(!v); setShowRoutes(!v); setShowLabels(!v); }}
                className={`px-2.5 py-1.5 font-medium cursor-pointer ${blueprint === v ? 'bg-blue-500 text-white' : 'bg-neutral-50 dark:bg-[#14171f] text-neutral-600 dark:text-neutral-300'}`}>{l}</button>
            ))}
          </div>
          {[
            { on: showContext, set: setShowContext, icon: Layers, label: 'Survey lines' },
            { on: showRoutes, set: setShowRoutes, icon: Waypoints, label: 'Q-line routes' },
            { on: showLabels, set: setShowLabels, icon: Tag, label: 'Labels' },
            { on: showHeat, set: setShowHeat, icon: Thermometer, label: 'Heat' },
            { on: assignMode, set: setAssignMode, icon: PencilRuler, label: 'Assign cameras' },
          ].map(b => (
            <button key={b.label} onClick={() => b.set(!b.on)}
              className={`px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 font-medium cursor-pointer ${b.on ? 'bg-blue-500 text-white border-blue-500' : 'bg-neutral-50 dark:bg-[#14171f] text-neutral-600 dark:text-neutral-300 border-neutral-200/70 dark:border-[#262b37]'}`}>
              <b.icon className="w-4 h-4" /> {b.label}
            </button>
          ))}
          <span className="mx-1 w-px h-5 bg-neutral-200 dark:bg-[#262b37]" />
          <button onClick={() => zoomBy(1.3)} title="Zoom in" className="p-1.5 rounded-md border border-neutral-200/70 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 cursor-pointer"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => zoomBy(1 / 1.3)} title="Zoom out" className="p-1.5 rounded-md border border-neutral-200/70 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 cursor-pointer"><ZoomOut className="w-4 h-4" /></button>
          <button onClick={reset} title="Reset view" className="p-1.5 rounded-md border border-neutral-200/70 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 cursor-pointer"><RotateCcw className="w-4 h-4" /></button>
          <button onClick={focusCorridor} className="px-2.5 py-1.5 font-medium rounded-md border border-neutral-200/70 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 cursor-pointer">Hill corridor</button>
        </div>
      </div>

      {/* people in each queue zone now, from the zones' start and end cameras (same figures as the Overview) */}
      <ZoneStrip variant="tiles" />

      <div className="min-h-0 grid grid-cols-1 xl:grid-cols-[1fr_300px] xl:grid-rows-[minmax(0,1fr)] gap-3 xl:h-[calc(100vh-330px)] xl:min-h-[520px]">
        {/* sketch */}
        <div ref={wrapRef} className="relative min-h-[420px] h-[60vh] xl:h-auto rounded-xl border border-neutral-200/80 dark:border-[#262b37] overflow-hidden" style={{ background: bg }}>
          <svg ref={svgRef} viewBox={`0 0 ${DATA.width} ${DATA.height}`} className="w-full h-full select-none"
            style={{ cursor: drag.current ? 'grabbing' : 'grab', touchAction: 'none' }}
            onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
            <rect x={0} y={0} width={DATA.width} height={DATA.height} fill={bg} />
            <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
              {/* CAD blueprint of the plan as the base layer */}
              {blueprint && <image href={BLUEPRINT.href} x={BLUEPRINT.x} y={BLUEPRINT.y} width={BLUEPRINT.width} height={BLUEPRINT.height} preserveAspectRatio="none" style={{ imageRendering: 'auto' }} />}
              {/* context linework from the survey drawing */}
              {showContext && Object.entries(DATA.context).map(([kind, lines]) => {
                const st = ctxStyle[kind] || ctxStyle.building;
                return (
                  <g key={kind} fill="none" stroke={st.stroke} strokeWidth={st.width / Math.sqrt(view.k)} strokeDasharray={st.dash} strokeLinejoin="round" opacity={kind === 'steps' ? 0.8 : 1}>
                    {lines.map((pl, i) => <polyline key={i} points={pl.map(p => p.join(',')).join(' ')} />)}
                  </g>
                );
              })}

              {/* heat halo under the Q-line routes */}
              {showHeat && DATA.routes.map(r => {
                const h = routeHeat[r.id]; const col = h ? heatColor(h.inView) : null;
                if (!col) return null;
                return <polyline key={'h' + r.id} points={r.points.map(p => p.join(',')).join(' ')} fill="none" stroke={col} strokeOpacity={0.35} strokeWidth={16 / Math.sqrt(view.k)} strokeLinecap="round" strokeLinejoin="round" />;
              })}
              {/* Q-line routes (traced) */}
              {showRoutes && DATA.routes.map(r => (
                <polyline key={r.id} points={r.points.map(p => p.join(',')).join(' ')} fill="none" stroke={catColor[r.category] || '#999'} strokeWidth={3.2 / Math.sqrt(view.k)} strokeLinecap="round" strokeLinejoin="round" />
              ))}

              {/* landmarks */}
              {showLabels && DATA.landmarks.filter(l => view.k >= 2.2 || (l.kind !== 'lane' && (view.k >= 1.5 || (l.kind !== 'control' && l.kind !== 'place')))).map((l, i) => {
                const fs = (l.kind === 'water' || l.kind === 'hill' ? 12.5 : l.kind === 'chainage' ? 8 : l.kind === 'lane' ? 7.5 : 9.5) / Math.sqrt(view.k);
                const col = l.kind === 'entrance' ? '#d946ef' : l.kind === 'exit' ? '#ef4444' : l.kind === 'control' ? '#f59e0b' : l.kind === 'water' ? (dark ? '#60a5fa' : '#2563eb') : l.kind === 'hill' ? (dark ? '#d97706' : '#b45309') : l.kind === 'chainage' ? (dark ? '#94a3b8' : '#64748b') : ink;
                return (
                  <g key={i} transform={`translate(${l.x} ${l.y})`}>
                    {(l.kind === 'entrance' || l.kind === 'exit' || l.kind === 'control') && <circle r={3 / Math.sqrt(view.k)} fill={col} />}
                    <text x={4 / Math.sqrt(view.k)} y={-2 / Math.sqrt(view.k)} fontSize={fs} fill={col} fontWeight={l.kind === 'entrance' || l.kind === 'water' || l.kind === 'hill' ? 700 : 500}
                      style={{ paintOrder: 'stroke', stroke: bg, strokeWidth: 2.5 / Math.sqrt(view.k), fontFamily: 'ui-sans-serif, system-ui' }}>
                      {l.kind === 'chainage' ? `+${l.name}` : l.name}
                    </text>
                  </g>
                );
              })}

              {/* camera positions */}
              {DATA.cameras.map(c => {
                const camId = mapping[c.key]; const lc = camId ? live[camId] : undefined;
                const col = camColor(lc, !!camId); const r = 7.5 / Math.sqrt(view.k);
                const isSel = selected === c.key;
                return (
                  <g key={c.key} transform={`translate(${c.x} ${c.y})`} style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => setHover({ key: c.key, cx: e.clientX, cy: e.clientY })}
                    onMouseMove={(e) => setHover({ key: c.key, cx: e.clientX, cy: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); assignMode ? setSelected(c.key) : openCamera(c.key); }}>
                    {lc?.state === 'online' && <circle r={r * 1.8} fill={col} opacity={0.18} />}
                    <circle r={r} fill={col} stroke={isSel ? '#0f172a' : blueprint ? '#ffffff' : bg} strokeWidth={(isSel ? 2 : 1.2) / Math.sqrt(view.k)} />
                    <text textAnchor="middle" dy={r * 0.38} fontSize={r * 1.08} fill="#fff" fontWeight={700} style={{ fontFamily: 'ui-monospace, monospace', pointerEvents: 'none' }}>{c.short}</text>
                  </g>
                );
              })}
            </g>
          </svg>

          {/* legend */}
          <div className="absolute left-2 bottom-2 rounded-lg bg-white/90 dark:bg-[#0f141c]/90 border border-neutral-200/80 dark:border-[#262b37] p-2.5 text-xs text-neutral-700 dark:text-neutral-300 backdrop-blur shadow-md">
            <div className="font-bold text-neutral-900 dark:text-neutral-100 text-xs sm:text-[13px] mb-1">Q-lines</div>
            {DATA.categories.filter(c => DATA.routes.some(r => r.category === c.id)).map(c => (
              <div key={c.id} className="flex items-center gap-1.5 py-0.5"><span className="inline-block w-5 h-1 rounded" style={{ background: c.color }} /> {c.name}</div>
            ))}
            <div className="font-bold text-neutral-900 dark:text-neutral-100 text-xs sm:text-[13px] mt-2 mb-1">Camera positions</div>
            {[['#22c55e', 'online'], ['#f59e0b', 'stale / connecting'], ['#38bdf8', 'view only (on demand)'], ['#ef4444', 'offline'], ['#64748b', 'not bound to a camera']].map(([c, t]) => (
              <div key={t} className="flex items-center gap-1.5 py-0.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c }} /> {t}</div>
            ))}
            {showHeat && <div className="mt-1.5 text-[11px] text-neutral-500">Heat: stretch colour from people in view (≥{HEAT.amber} amber, ≥{HEAT.red} red)</div>}
          </div>

          {/* hover card */}
          {hover && hoverPos && wrapRef.current && (() => {
            const r = wrapRef.current!.getBoundingClientRect();
            const left = Math.min(hover.cx - r.left + 14, r.width - 270), top = Math.min(hover.cy - r.top + 14, r.height - 140);
            return (
              <div className="absolute pointer-events-none z-20 w-[260px] rounded-lg bg-white dark:bg-[#0f141c] border border-neutral-200 dark:border-[#262b37] shadow-xl p-3 text-xs" style={{ left, top }}>
                <div className="font-bold text-sm text-neutral-900 dark:text-neutral-100">Camera position {hoverPos.key}</div>
                {mapping[hoverPos.key] ? (
                  <>
                    <div className="mt-0.5 text-neutral-600 dark:text-neutral-300 truncate">{hoverCam?.name || mapping[hoverPos.key]} <span className="font-mono text-neutral-400">{hoverCam?.ip}</span></div>
                    <div className="mt-1.5 grid grid-cols-3 gap-1.5 font-mono text-[11.5px]">
                      <div><div className="text-neutral-400 text-[10.5px]">state</div><div style={{ color: camColor(hoverCam, true) }}>{hoverCam?.state || 'unknown'}</div></div>
                      <div><div className="text-neutral-400 text-[10.5px]">in view</div><div className="text-neutral-800 dark:text-neutral-100">{fmt(hoverCam?.tracks)}</div></div>
                      <div><div className="text-neutral-400 text-[10.5px]">15 min</div><div className="text-neutral-800 dark:text-neutral-100">{fmt(hoverCam?.m15.toward)} / {fmt(hoverCam?.m15.away)}</div></div>
                    </div>
                    <div className="mt-1.5 font-mono text-neutral-500 text-[11.5px]">today <b className="text-emerald-500">{fmt(hoverCam?.today.toward)}</b> toward · <b className="text-sky-500">{fmt(hoverCam?.today.away)}</b> away</div>
                    <div className="mt-1 text-[11px] text-neutral-400">click for live view</div>
                  </>
                ) : (
                  <div className="mt-1 text-neutral-500">No camera bound yet · use "Assign cameras" to bind one</div>
                )}
              </div>
            );
          })()}
        </div>

        {/* side panel */}
        <div className="min-h-0 flex flex-col gap-3 max-h-[70vh] xl:max-h-none">
          {selPos && (
            <div className="rounded-xl border border-blue-300/60 dark:border-blue-500/30 bg-blue-50/60 dark:bg-blue-500/5 p-3 text-xs sm:text-[13px]">
              <div className="flex items-center justify-between">
                <div className="font-bold text-sm sm:text-base text-neutral-900 dark:text-neutral-100">Position {selPos.key}</div>
                <button onClick={() => setSelected(null)} className="p-0.5 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 cursor-pointer"><X className="w-4 h-4" /></button>
              </div>
              <label className="block mt-2 font-medium text-neutral-500 dark:text-neutral-400">Bound camera</label>
              <select value={mapping[selPos.key] || ''} onChange={e => saveMapping(selPos.key, e.target.value)}
                className="mt-1 w-full rounded-md border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#14171f] text-neutral-800 dark:text-neutral-100 px-2.5 py-1.5 text-xs sm:text-[13px]">
                <option value="">— not bound —</option>
                {configCams.map(c => <option key={c.id} value={c.id}>{c.id} · {c.name}</option>)}
              </select>
              {mapping[selPos.key] && <button onClick={() => openCamera(selPos.key)} className="mt-2 w-full rounded-md bg-blue-500 text-white py-1.5 font-semibold text-xs sm:text-[13px] cursor-pointer">Open live view</button>}
            </div>
          )}
          <div className="flex-1 min-h-0 rounded-xl border border-neutral-200/80 dark:border-[#262b37] bg-white dark:bg-[#0f141c] flex flex-col">
            <div className="px-3.5 py-2.5 border-b border-neutral-200/80 dark:border-[#262b37] text-xs sm:text-[13px] font-bold text-neutral-800 dark:text-neutral-100 flex items-center justify-between">
              <span>Camera positions on the plan</span><span className="font-mono text-neutral-400 text-xs font-normal">{boundCount}/{DATA.cameras.length} bound</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {DATA.cameras.map(c => {
                const camId = mapping[c.key]; const lc = camId ? live[camId] : undefined;
                return (
                  <div key={c.key} onClick={() => { setSelected(c.key); }}
                    className={`px-3 py-2 border-b border-neutral-100 dark:border-[#1b2029] flex items-center gap-2 text-xs sm:text-[13px] cursor-pointer hover:bg-neutral-50 dark:hover:bg-[#141922] ${selected === c.key ? 'bg-blue-50 dark:bg-blue-500/10' : ''}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: camColor(lc, !!camId) }} />
                    <span className="font-mono font-semibold w-16 shrink-0 text-neutral-800 dark:text-neutral-100">{c.key.replace('No. ', '#')}</span>
                    <span className="truncate flex-1 text-neutral-600 dark:text-neutral-300">{camId ? (lc?.name || camId) : 'not bound'}</span>
                    {lc && <span className="font-mono text-neutral-500 dark:text-neutral-400 shrink-0 text-xs">{lc.tracks == null ? '—' : lc.tracks} in view</span>}
                    {camId && (
                      <button type="button" onClick={e => { e.stopPropagation(); setSelected(c.key); openCamera(c.key); }}
                        title={`Open the live view of ${lc?.name || camId}`} aria-label={`Live view of ${lc?.name || camId}`}
                        className="p-1 rounded bg-blue-500 hover:bg-blue-600 text-white shrink-0 cursor-pointer active:scale-95">
                        <svg viewBox="0 0 24 16" width="13" height="9" aria-hidden="true"><path d="M1 3h13v10H1z" fill="currentColor" /><path d="M15 6l7-4v12l-7-4z" fill="currentColor" /></svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {modal && (
        <CameraStreamModal checkpoint={modal.cp} initialCameraId={modal.camId} onClose={() => setModal(null)} />
      )}
    </div>
  );
};
